import {
    OptionQuoteViewModel,
    QuoteViewModel,
    TmcQuoteViewModel,
    UnderlyingQuoteViewModel
} from "app/quote/models/QuoteViewModel";
import {
    EquityOrderViewModel,
    OptionOrderViewModel,
    OrderViewModel,
    TmcOrderLegViewModel,
    TmcOrderViewModel,
    WarrantOrderViewModel
} from "app/orders/models/OrderViewModel";
import {isIressOrderModel} from "app/orders/models/iress/IressOrderViewModel";
import {PriceStrategy} from "../../../components/inputs/price/input/PriceStrategy";
import {AccountBriefRecord} from "../../../account/types/AccountBriefRecord";
import {OptionRecord} from "../../../options/contracts/models/OptionChainRecord";
import {SimulatedOrderId, SimulatedOrderRecord, SimulatedTmcOrderRecord} from "./SimulatedOrderRecord";
import {OrderConditions} from "../OrderConditions";

export type SimulatedOrderViewModels = ReadonlyArray<SimulatedOrderViewModel>;

export interface SimulatedOrderViewModel<Q extends QuoteViewModel = QuoteViewModel> extends OrderViewModel<Q> {
    readonly id: SimulatedOrderId;
}

export function isSimulatedOrderModel(model: OrderViewModel): model is SimulatedOrderViewModel {
    return !isIressOrderModel(model);
}

export interface ConditionalSimulatedOrderViewModel<Q extends QuoteViewModel = QuoteViewModel> extends SimulatedOrderViewModel<Q> {
    readonly conditions: OrderConditions;
}

export function isConditionalSimulatedOrderViewModel(model: OrderViewModel): model is ConditionalSimulatedOrderViewModel {
    return ("conditions" in model) && model["conditions"] != null;
}

export class SimulatedOptionOrderViewModel extends OptionOrderViewModel implements SimulatedOrderViewModel {

    public readonly id: SimulatedOrderId;
    public readonly averagePrice: number;

    protected constructor(order: SimulatedOrderRecord,
                          isAmendable: boolean,
                          quote: OptionQuoteViewModel | null,
                          priceStrategy: PriceStrategy,
                          account: AccountBriefRecord | null,
                          isMine: boolean,
                          contract: OptionRecord | null) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, true, contract);
        this.id = order.id;
        this.averagePrice = order.averagePrice ?? 0;
    }

    public static of(order: SimulatedOrderRecord,
                     isAmendable: boolean,
                     quote: OptionQuoteViewModel | null,
                     priceStrategy: PriceStrategy,
                     account: AccountBriefRecord | null,
                     isMine: boolean,
                     contract: OptionRecord | null): SimulatedOptionOrderViewModel {
        return new SimulatedOptionOrderViewModel(order, isAmendable, quote, priceStrategy, account, isMine, contract);
    }
}

export function isSimulatedOptionOrder(model: OrderViewModel): model is SimulatedOptionOrderViewModel {
    return model instanceof SimulatedOptionOrderViewModel;
}

export class ConditionalSimulatedOptionOrderViewModel extends SimulatedOptionOrderViewModel implements ConditionalSimulatedOrderViewModel<OptionQuoteViewModel> {

    private constructor(order: SimulatedOrderRecord,
                        isAmendable: boolean,
                        quote: OptionQuoteViewModel | null,
                        priceStrategy: PriceStrategy,
                        account: AccountBriefRecord | null,
                        isMine: boolean,
                        contract: OptionRecord | null,
                        public readonly conditions: OrderConditions) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, contract);
    }

    public static from(order: SimulatedOrderRecord,
                       isAmendable: boolean,
                       quote: OptionQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       contract: OptionRecord | null,
                       conditions: OrderConditions): ConditionalSimulatedOptionOrderViewModel {
        return new ConditionalSimulatedOptionOrderViewModel(order,
                                                            isAmendable,
                                                            quote,
                                                            priceStrategy,
                                                            account,
                                                            isMine,
                                                            contract,
                                                            conditions);
    }
}

export function isConditionalSimulatedOptionOrder(model: SimulatedOrderViewModel): model is ConditionalSimulatedOptionOrderViewModel {
    return isSimulatedOptionOrder(model) && isConditionalSimulatedOrderViewModel(model);
}

export class SimulatedTmcOrderViewModel extends TmcOrderViewModel implements SimulatedOrderViewModel {

    public readonly id: SimulatedOrderId;
    public readonly averagePrice: number;

    protected constructor(order: SimulatedOrderRecord,
                          isAmendable: boolean,
                          quote: TmcQuoteViewModel | null,
                          priceStrategy: PriceStrategy,
                          account: AccountBriefRecord | null,
                          isMine: boolean,
                          legs: ReadonlyArray<TmcOrderLegViewModel>) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, true, legs);
        this.id = order.id;
        this.averagePrice = order.averagePrice ?? 0;
    }

    public static of(order: SimulatedTmcOrderRecord,
                     isAmendable: boolean,
                     quote: TmcQuoteViewModel | null,
                     priceStrategy: PriceStrategy,
                     account: AccountBriefRecord | null,
                     isMine: boolean,
                     legs: ReadonlyArray<TmcOrderLegViewModel>): SimulatedTmcOrderViewModel {
        return new SimulatedTmcOrderViewModel(order,
                                              isAmendable,
                                              quote,
                                              priceStrategy,
                                              account,
                                              isMine,
                                              legs);
    }
}

export function isSimulatedTmcOrder(model: OrderViewModel): model is SimulatedTmcOrderViewModel {
    return model instanceof SimulatedTmcOrderViewModel;
}

export class ConditionalSimulatedTmcOrderViewModel extends SimulatedTmcOrderViewModel implements ConditionalSimulatedOrderViewModel<TmcQuoteViewModel> {

    private constructor(order: SimulatedTmcOrderRecord,
                        isAmendable: boolean,
                        quote: TmcQuoteViewModel | null,
                        priceStrategy: PriceStrategy,
                        account: AccountBriefRecord | null,
                        isMine: boolean,
                        legs: ReadonlyArray<TmcOrderLegViewModel>,
                        public readonly conditions: OrderConditions) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, legs);
    }

    public static from(order: SimulatedTmcOrderRecord,
                       isAmendable: boolean,
                       quote: TmcQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       legs: ReadonlyArray<TmcOrderLegViewModel>,
                       conditions: OrderConditions): ConditionalSimulatedTmcOrderViewModel {
        return new ConditionalSimulatedTmcOrderViewModel(order,
                                                         isAmendable,
                                                         quote,
                                                         priceStrategy,
                                                         account,
                                                         isMine,
                                                         legs,
                                                         conditions);
    }
}

export function isConditionalSimulatedTmcOrderModel(model: SimulatedOrderViewModel): model is ConditionalSimulatedTmcOrderViewModel {
    return isSimulatedTmcOrder(model) && isConditionalSimulatedOrderViewModel(model);
}

export class SimulatedEquityOrderViewModel extends EquityOrderViewModel implements SimulatedOrderViewModel {

    public readonly id: SimulatedOrderId;
    public readonly averagePrice: number;

    protected constructor(order: SimulatedOrderRecord,
                          isAmendable: boolean,
                          quote: UnderlyingQuoteViewModel | null,
                          priceStrategy: PriceStrategy,
                          account: AccountBriefRecord | null,
                          isMine: boolean) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, true);
        this.id = order.id;
        this.averagePrice = order.averagePrice ?? 0;
    }

    public static of(order: SimulatedOrderRecord,
                     isAmendable: boolean,
                     quote: UnderlyingQuoteViewModel | null,
                     priceStrategy: PriceStrategy,
                     account: AccountBriefRecord | null,
                     isMine: boolean): SimulatedEquityOrderViewModel {
        return new SimulatedEquityOrderViewModel(order, isAmendable, quote, priceStrategy, account, isMine);
    }
}

export function isSimulatedEquityOrder(model: OrderViewModel): model is SimulatedEquityOrderViewModel {
    return model instanceof SimulatedEquityOrderViewModel;
}

export class ConditionalSimulatedEquityOrderViewModel extends SimulatedEquityOrderViewModel implements ConditionalSimulatedOrderViewModel<UnderlyingQuoteViewModel> {

    private constructor(order: SimulatedOrderRecord,
                        isAmendable: boolean,
                        quote: UnderlyingQuoteViewModel | null,
                        priceStrategy: PriceStrategy,
                        account: AccountBriefRecord | null,
                        isMine: boolean,
                        public readonly conditions: OrderConditions) {
        super(order, isAmendable, quote, priceStrategy, account, isMine);
    }

    public static from(order: SimulatedOrderRecord,
                       isAmendable: boolean,
                       quote: UnderlyingQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       conditions: OrderConditions): ConditionalSimulatedEquityOrderViewModel {
        return new ConditionalSimulatedEquityOrderViewModel(order,
                                                            isAmendable,
                                                            quote,
                                                            priceStrategy,
                                                            account,
                                                            isMine,
                                                            conditions);
    }
}

export function isConditionalSimulatedEquityOrderModel(model: SimulatedOrderViewModel): model is ConditionalSimulatedEquityOrderViewModel {
    return isSimulatedEquityOrder(model) && isConditionalSimulatedOrderViewModel(model);
}


export class SimulatedWarrantOrderViewModel extends WarrantOrderViewModel implements SimulatedOrderViewModel {

    public readonly id: SimulatedOrderId;
    public readonly averagePrice: number;

    protected constructor(order: SimulatedOrderRecord,
                          isAmendable: boolean,
                          quote: UnderlyingQuoteViewModel | null,
                          priceStrategy: PriceStrategy,
                          account: AccountBriefRecord | null,
                          isMine: boolean) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, true);
        this.id = order.id;
        this.averagePrice = order.averagePrice ?? 0;
    }

    public static of(order: SimulatedOrderRecord,
                     isAmendable: boolean,
                     quote: UnderlyingQuoteViewModel | null,
                     priceStrategy: PriceStrategy,
                     account: AccountBriefRecord | null,
                     isMine: boolean): SimulatedWarrantOrderViewModel {
        return new SimulatedWarrantOrderViewModel(order, isAmendable, quote, priceStrategy, account, isMine);
    }
}

export function isSimulatedWarrantOrder(model: OrderViewModel): model is SimulatedWarrantOrderViewModel {
    return model instanceof SimulatedWarrantOrderViewModel;
}

export class ConditionalSimulatedWarrantOrderViewModel extends SimulatedWarrantOrderViewModel implements ConditionalSimulatedOrderViewModel<UnderlyingQuoteViewModel> {

    private constructor(order: SimulatedOrderRecord,
                        isAmendable: boolean,
                        quote: UnderlyingQuoteViewModel | null,
                        priceStrategy: PriceStrategy,
                        account: AccountBriefRecord | null,
                        isMine: boolean,
                        public readonly conditions: OrderConditions) {
        super(order, isAmendable, quote, priceStrategy, account, isMine);
    }

    public static from(order: SimulatedOrderRecord,
                       isAmendable: boolean,
                       quote: UnderlyingQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       conditions: OrderConditions): ConditionalSimulatedWarrantOrderViewModel {
        return new ConditionalSimulatedWarrantOrderViewModel(order,
                                                             isAmendable,
                                                             quote,
                                                             priceStrategy,
                                                             account,
                                                             isMine,
                                                             conditions);
    }
}

export function isConditionalSimulatedWarrantOrderModel(model: SimulatedOrderViewModel): model is ConditionalSimulatedWarrantOrderViewModel {
    return isSimulatedWarrantOrder(model) && isConditionalSimulatedOrderViewModel(model);
}

